import React, { useEffect, useMemo, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';

import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';

import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';

import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/directionality';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';

import { uploadsService, API_ORIGIN } from '../services/api';

export interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  height?: number;
  placeholder?: string;
}

/**
 * TinyMCE — ویرایشگر WYSIWYG معروف، کاملاً لوکال از طریق npm (بدون CDN).
 * مجوز GPL برای میزبانی خودکار: {@link https://www.tiny.cloud/docs/tinymce/latest/license-key/}
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  height = 400,
  placeholder = 'محتوای خود را وارد کنید...',
}) => {
  /** همیشه جدیدترین onChange؛ init تین‌ام‌سی‌ای فقط یک بار ساخته می‌شود */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const init = useMemo(() => {
    const images_upload_handler = async (blobInfo: { blob: () => Blob; filename: () => string }) => {
      const blob = blobInfo.blob();
      const name = blobInfo.filename() || 'upload.png';
      const file = new File([blob], name, { type: blob.type || 'image/png' });

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('حجم فایل نباید بیشتر از ۱۰ مگابایت باشد');
      }
      if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/i)) {
        throw new Error('فقط تصویر JPG، PNG، GIF یا WebP مجاز است');
      }

      const res = await uploadsService.uploadImage(file);
      let url = res.original || res.processed || res.thumbnail;
      if (!url || typeof url !== 'string') {
        throw new Error('آدرس تصویر از سرور دریافت نشد');
      }
      if (url.startsWith('/')) {
        url = `${API_ORIGIN}${url}`;
      }
      return url;
    };

    const flushToReact = (editor: { getContent: () => string }) => {
      onChangeRef.current(editor.getContent());
    };

    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const base_url =
      typeof window !== 'undefined'
        ? `${window.location.origin}${publicUrl}/tinymce`
        : `${publicUrl}/tinymce`;

    return {
      height,
      /** Lazy-loaded plugin assets (e.g. help i18n) must be served as real JS, not CRA index.html */
      base_url,
      menubar: 'file edit view insert format tools table help',
      promotion: false,
      branding: false,
      skin: false,
      content_css: false,
      statusbar: true,
      resize: true,
      directionality: 'rtl' as const,
      placeholder,
      plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'visualchars',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
        'directionality',
        'emoticons',
      ],
      toolbar:
        'undo redo | blocks | fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ltr rtl | link image media table emoticons charmap | code visualblocks visualchars | removeformat | fullscreen help',
      block_formats: 'Paragraph=p; Heading 2=h2; Heading 3=h3; Heading 4=h4; Preformatted=pre',
      font_family_formats:
        'Vazirmatn=Vazirmatn,Segoe UI,Tahoma,sans-serif; Arial=arial,helvetica,sans-serif; Tahoma=tahoma,arial,helvetica,sans-serif; Georgia=georgia,palatino,serif',
      fontsize_formats: '12px 14px 16px 18px 20px 24px 28px 32px',
      content_style:
        "body { font-family: 'Vazirmatn','Segoe UI',Tahoma,Arial,sans-serif; font-size: 15px; line-height: 1.75; direction: rtl; text-align: right; color: #0f172a; }",
      image_advtab: true,
      image_caption: true,
      automatic_uploads: true,
      images_upload_handler,
      file_picker_types: 'image media',
      link_default_target: '_blank',
      rel_list: [
        { title: 'nofollow', value: 'nofollow' },
        { title: 'بدون rel', value: '' },
      ],
      relative_urls: false,
      convert_urls: true,
      setup: (editor: { on: (ev: string, fn: () => void) => void; getContent: () => string }) => {
        /** کلیک ذخیره معمولاً blur می‌فرستد و آخرین HTML به React همگام می‌شود */
        editor.on('blur', () => flushToReact(editor));
      },
    };
  }, [height, placeholder]);

  return (
    <div className="tinymce-rich-editor">
      <Editor
        licenseKey="gpl"
        value={value}
        onEditorChange={(content) => {
          onChangeRef.current(content);
        }}
        init={init}
      />
    </div>
  );
};

export default RichTextEditor;
